import React, { useState } from "react";
import { PenLine } from "lucide-react";
import { toast } from "sonner";

import { BrandButton } from "@/shared/components/ui-kit";
import { inputCls } from "@/shared/components/task-atoms";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/shared/components/ui/dialog";
import { useCreateKbNote } from "@/shared/api/knowledge";

/* Stays open on failure so nothing typed is lost. */
export function KbNoteDialog({ kbId, parentId, onClose }: { kbId: string; parentId: string | null; onClose: () => void }) {
  const [name, setName] = useState("");
  const [content, setContent] = useState("");
  const create = useCreateKbNote();
  const trimmed = name.trim();

  const save = () => {
    if (!trimmed || create.isPending) return;
    create.mutate(
      { kbId, body: { name: trimmed, content, parent_id: parentId ?? undefined } },
      { onSuccess: onClose, onError: (e) => toast.error(e.message) },
    );
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-[480px] p-0 gap-0 rounded-[16px] bg-[var(--canvas)] border-[var(--hairline-strong)] shadow-dialog overflow-hidden">
        <div className="px-5 pt-5 pb-4 border-b hairline">
          <DialogTitle className="text-[15px] font-semibold tracking-[-0.01em] text-[var(--text-primary)] flex items-center gap-2">
            <PenLine className="w-4 h-4 text-[var(--text-tertiary)]" strokeWidth={1.75} />
            Write a note
          </DialogTitle>
          <DialogDescription className="text-[12.5px] text-[var(--text-tertiary)] mt-1">
            Quick context the agent should keep in mind — style guides, FAQs, decisions.
          </DialogDescription>
        </div>
        <div className="px-5 py-4 space-y-3">
          <input autoFocus aria-label="Note title" value={name} onChange={(e) => setName(e.target.value)} placeholder="Note title" className={inputCls} />
          <textarea
            aria-label="Note body"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Write anything…"
            rows={6}
            className="w-full px-2.5 py-2 rounded-[8px] bg-[var(--surface-sunk)] ring-1 ring-[var(--hairline)] focus:ring-[var(--hairline-focus)] outline-none text-[13px] leading-[1.55] text-[var(--text-primary)] placeholder:text-[var(--text-quaternary)] resize-none"
          />
        </div>
        <div className="px-5 py-3.5 border-t hairline flex items-center justify-end gap-2">
          <button onClick={onClose} className="press h-8 px-3.5 rounded-[8px] text-[13px] font-medium text-[var(--text-secondary)] hover:bg-[var(--accent-hover)] hover:text-[var(--text-primary)]">
            Cancel
          </button>
          <BrandButton onClick={save} disabled={!trimmed || create.isPending} className="disabled:opacity-40 disabled:pointer-events-none">
            Save note
          </BrandButton>
        </div>
      </DialogContent>
    </Dialog>
  );
}
